import { TextField, TextFieldProps } from '@mui/material'
import { useCallback } from 'react'

type Props = TextFieldProps

/**
 * `CustomAmountTextField` は、金額入力などでよくある「0初期値」の扱いに特化したカスタム MUI TextField コンポーネントです。
 *
 * 主な特徴:
 * - フォーカス時に値が "0" であれば空文字に変換します（ユーザーがそのまま入力しやすくするため）。
 * - ブラー時に値が空文字であれば "0" に自動補完します。
 * - `onChange`, `onFocus`, `onBlur` などのイベントは元の `TextFieldProps` と互換性があります。
 *
 * @component
 * @param {TextFieldProps} props - MUI の `TextField` に渡せるすべてのプロパティに加えて、金額入力特有の動作を上書きします。
 * @returns {JSX.Element} カスタム動作付きの MUI `TextField` コンポーネント
 *
 * @example
 * ```tsx
 * <CustomAmountTextField
 *   label="金額"
 *   fullWidth
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 * />
 * ```
 */
export const CustomAmountTextField: React.FC<Props> = (props) => {
  const onChangeWrapper = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = e.target.value
      props.onChange?.({
        ...e,
        target: {
          ...e.target,
          value: val === '' ? '0' : val,
        },
      })
    },
    [props],
  )

  return (
    <TextField
      {...props}
      onFocus={(e) => {
        if (e.target.value === '0') {
          props.onChange?.({
            ...e,
            target: {
              ...e.target,
              value: '',
            },
          })
        }
        props.onFocus?.(e)
      }}
      onBlur={(e) => {
        onChangeWrapper(e)
        props.onBlur?.(e)
      }}
    />
  )
}
